import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, BookOpen, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Chapters = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChapters();
    fetchProgress();
  }, [level]);

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API}/chapters?level=${level}`);
      setChapters(response.data);
    } catch (error) {
      console.error('Failed to fetch chapters', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress`);
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress', error);
    }
  };

  const isCompleted = (chapterId) => {
    return progress.some(p => p.chapter_id === chapterId && p.completed);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard-button"
          className="mb-8 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-12" data-testid="chapters-header">
          <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Learning Path</span>
          <h1 className="text-4xl md:text-5xl font-grotesk font-bold text-white mt-2 mb-4 tracking-tight capitalize">
            {level} <span className="text-primary">Level</span>
          </h1>
          <p className="text-zinc-400">{chapters.length} chapters to master</p>
        </div>

        <div className="space-y-4">
          {chapters.map((chapter) => (
            <button
              key={chapter.id}
              onClick={() => navigate(`/chapter/${chapter.id}`)}
              data-testid={`chapter-${chapter.id}-button`}
              className="w-full bg-zinc-900/30 border border-white/10 rounded-xl p-6 hover:border-primary/30 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 text-left group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-xs font-mono uppercase tracking-widest text-primary/80">
                      Chapter {chapter.order}
                    </span>
                    {isCompleted(chapter.id) && (
                      <div className="flex items-center space-x-1 text-primary" data-testid={`chapter-${chapter.id}-completed`}>
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs font-mono">Completed</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-grotesk font-semibold text-white group-hover:text-primary mb-2">
                    {chapter.title}
                  </h3>
                  <p className="text-sm text-zinc-400 mb-3 leading-relaxed">{chapter.summary}</p>
                  <div className="flex items-center space-x-4 text-xs text-zinc-500">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{chapter.estimated_time} min</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <BookOpen className="w-3 h-3" />
                      <span>{chapter.key_points?.length || 0} key points</span>
                    </div>
                  </div>
                </div>
                <div className="ml-4 text-3xl font-mono text-primary/30 group-hover:text-primary">
                  {String(chapter.order).padStart(2, '0')}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Chapters;
